import { useCallback, useEffect, useRef } from 'react'
import {
  announce,
  CLEAR_ANNOUNCEMENT_AFTER_MS,
} from '~/components/shared/LiveAnnouncer'
import { useCallPopEnabled } from '~/hooks/useCallPopEnabled'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { isElectron } from '~/utils/common'

import { RelayedCallAnnouncement } from '@mangovoice/types'

let nextRelayId = 0

/**
 * Returns a function that announces a call failure assertively where the user
 * will hear it. Call actions run in the main window even when they were
 * triggered from the call-pop, so when call pop is enabled and the main window
 * isn't focused, the message is handed to the popup instead of being spoken
 * here; this way it is announced once.
 */
export const useAnnounceCallFailure = () => {
  const callPopEnabled = useCallPopEnabled()
  const [, setRelayedAnnouncement] =
    useSharedVariable<RelayedCallAnnouncement | null>('CallAnnouncement', null)

  return useCallback(
    (message: string) => {
      if (isElectron && callPopEnabled && !document.hasFocus()) {
        nextRelayId += 1
        setRelayedAnnouncement({
          id: `${Date.now()}-${nextRelayId}`,
          message,
          sentAt: Date.now(),
        })
        return
      }
      announce(message, 'assertive')
    },
    [callPopEnabled, setRelayedAnnouncement],
  )
}

/**
 * Speaks, in the call-pop window, the failures the main window relays with
 * `useAnnounceCallFailure`. A message older than the announcer's clear time
 * (e.g. the last value when the popup opens) is ignored.
 */
export const useRelayedCallAnnouncements = () => {
  const [relayed] = useSharedVariable<RelayedCallAnnouncement | null>(
    'CallAnnouncement',
    null,
  )
  const lastAnnouncedIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!relayed || relayed.id === lastAnnouncedIdRef.current) return
    lastAnnouncedIdRef.current = relayed.id
    if (Date.now() - relayed.sentAt > CLEAR_ANNOUNCEMENT_AFTER_MS) return
    announce(relayed.message, 'assertive')
  }, [relayed])
}
